import React, { useEffect, useState } from "react";
import { Container, Card, Alert } from "react-bootstrap";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../context/NotificationContext";
import "../css/Notifications.css";
import { formatDateTime } from "../helpers/dateFormat";

const Notifications = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { setNotificationCount } = useNotification();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_APP_URL}/notifications`,
          {
            headers: {
              "Content-Type": "application/json",
              "X-Platform": process.env.REACT_APP_X_PLATFORM,
              "API-Key": process.env.REACT_APP_API_KEY,
              "X-API-Version": process.env.REACT_APP_X_API_VERSION,
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            withCredentials: true,
          }
        );

        if (response.data.success) {
          setNotifications(response.data.data);
          const unreadCount = response.data.data.filter(
            (notif) => !notif.read_at
          ).length;
          setNotificationCount(unreadCount);
        } else {
          setError(response.data.message);
        }
      } catch (err) {
        setError(t("errorFetchingNotifications"));
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [i18n.language, setNotificationCount]);

  const handleClick = async (id) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_APP_URL}/notifications/${id}`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Platform": process.env.REACT_APP_X_PLATFORM,
            "API-Key": process.env.REACT_APP_API_KEY,
            "X-API-Version": process.env.REACT_APP_X_API_VERSION,
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        setNotifications((prevNotifications) =>
          prevNotifications.map((notif) =>
            notif.id === id
              ? { ...notif, read_at: new Date().toISOString() }
              : notif
          )
        );

        setNotificationCount((prevCount) =>
          prevCount > 0 ? prevCount - 1 : 0
        );

        navigate(`/dashboard/notifications/${id}`);
      } else {
        console.error("Failed to fetch notification:", response.data.message);
      }
    } catch (err) {
      console.error("Error fetching notification:", err);
    }
  };

  if (loading) {
    return (
      <Container className="notifications-page-container">
        <div className="notifications-loading">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="notifications-shimmer"></div>
          ))}
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="notifications-page-container">
        <Alert variant="danger" className="text-center my-5">
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="notifications-page-container">
      <h2 className="notifications-title">
        {i18n.language === "ID" ? "Notifikasi" : t("notifications")}
      </h2>
      <div className="notifications-list">
        {notifications.length === 0 ? (
          <p className="text-center text-muted">{t("noNotifications")}</p>
        ) : (
          notifications.map((notification) => (
            <Card
              key={notification.id}
              className={`notifications-card ${
                notification.read_at ? "read" : "unread"
              }`}
              onClick={() => handleClick(notification.id)}
            >
              <Card.Body>
                <Card.Title
                  className={`notifications-card-title ${
                    !notification.read_at ? "unread-title" : ""
                  }`}
                >
                  {notification.title}
                </Card.Title>
                <Card.Text className="notifications-card-message">
                  {notification.message}
                </Card.Text>
              </Card.Body>
              <Card.Footer className="notifications-card-footer">
                <small className="text-muted">
                  {formatDateTime(notification.created_at)}
                </small>
              </Card.Footer>
            </Card>
          ))
        )}
      </div>
    </Container>
  );
};

export default Notifications;
